import {
  type ApiErrorResponse,
  isApiErrorResponse as isStrictApiErrorResponse,
  isErrorCode,
} from "@vardic/shared/error-codes";
import type { ErrorCode } from "@vardic/shared/error-codes";
import i18n from "@/locales";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: ApiErrorResponse,
    public code?: ErrorCode,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Loose error payload: what a caller can rely on after decoding a non-ok
 * response body that may not carry the full strict `ApiErrorResponse` shape.
 */
export type LooseApiErrorResponse = Partial<
  Pick<ApiErrorResponse, "error" | "message" | "code">
>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function isApiErrorResponse(
  value: unknown,
): value is LooseApiErrorResponse {
  if (isStrictApiErrorResponse(value)) {
    return true;
  }

  if (!isRecord(value)) {
    return false;
  }

  const hasStringError = typeof value.error === "string";
  const hasStringMessage = typeof value.message === "string";
  const hasValidCode = value.code === undefined ||
    typeof value.code === "string";

  return (hasStringError || hasStringMessage) && hasValidCode;
}

/**
 * Strict decoder used by the shared API client: only payloads that carry a
 * known `ErrorCode` (plus prose) become an `ApiErrorResponse`; anything else is
 * discarded so the client falls back to the HTTP status text.
 */
export function normalizeApiErrorResponse(
  value: unknown,
): ApiErrorResponse | undefined {
  if (isStrictApiErrorResponse(value)) {
    return value;
  }

  if (!isRecord(value) || !isErrorCode(value.code)) {
    return undefined;
  }

  const error = typeof value.error === "string"
    ? value.error
    : typeof value.message === "string"
    ? value.message
    : undefined;
  const message = typeof value.message === "string"
    ? value.message
    : typeof value.error === "string"
    ? value.error
    : undefined;

  if (!error || !message) {
    return undefined;
  }

  return {
    error,
    message,
    code: value.code,
    details: isRecord(value.details) ? value.details : undefined,
    failureCode: typeof value.failureCode === "string"
      ? value.failureCode
      : undefined,
  };
}

/**
 * Lenient decoder for call sites that hand-roll `fetch` and only need prose to
 * show the user. Returns null when the body is empty or unrecognizable.
 */
export async function parseApiErrorResponse(
  response: Response,
): Promise<LooseApiErrorResponse | null> {
  const payload: unknown = await response.json().catch(() => null);
  if (!payload) {
    return null;
  }

  if (typeof payload === "string") {
    return { error: payload, message: payload };
  }

  return isApiErrorResponse(payload) ? payload : null;
}

export function getApiErrorMessage(
  errorData: LooseApiErrorResponse | null,
  fallback: string,
): string {
  if (!errorData) return fallback;
  // Prefer a localized message keyed by the backend error code, so a Polish
  // user never sees raw English prose for any code we translate. Falls back to
  // the backend prose (then the caller's fallback) for codes we do not map.
  if (errorData.code) {
    const key = `errors:codes.${errorData.code}`;
    if (i18n.exists(key)) {
      return i18n.t(key);
    }
  }
  return errorData.message ?? errorData.error ?? fallback;
}
